// The UST API has no severity field, so severity is derived from the latest
// hourly values. Bands follow the European Air Quality Index (hourly, µg/m³).
// H2S is not part of the EAQI; its bands are an approximation anchored on the
// Icelandic 24h limit (50 µg/m³) and UST health guidance for higher levels.
const BANDS = {
  "PM2.5": [10, 20, 25, 50, 75],
  PM10: [20, 40, 50, 100, 150],
  NO2: [40, 90, 120, 230, 340],
  O3: [50, 100, 130, 240, 380],
  SO2: [100, 200, 350, 500, 750],
  H2S: [50, 150, 300, 600, 1000],
};

// Ordinal ramp drawn from Icelandic geology (moss, lichen, sulphur, ember,
// lava, basalt). Lightness falls steadily with severity so the order reads
// without hue; validated with the dataviz ordinal checks on the land colour.
export const LEVELS = [
  { label: "Good", color: "#5fb479" },
  { label: "Fair", color: "#879131" },
  { label: "Moderate", color: "#a16803" },
  { label: "Poor", color: "#a63d02" },
  { label: "Very poor", color: "#8f1b22" },
  { label: "Extremely poor", color: "#631b3d" },
];

export const NO_DATA = { label: "No recent data", color: "#8a979c" };

// Readings older than this are treated as stale.
const MAX_AGE_MS = 3 * 60 * 60 * 1000;

const levelFor = (param, value) => {
  const bands = BANDS[param];
  if (!bands || !Number.isFinite(value)) return null;
  const index = bands.findIndex((limit) => value < limit);
  return index === -1 ? bands.length : index;
};

// API timestamps are local Icelandic time, which is UTC year-round.
const parseTime = (endtime) => new Date(`${endtime.replace(" ", "T")}Z`);

const HOUR_MS = 60 * 60 * 1000;

const hasValue = (reading) =>
  reading && reading.value !== null && reading.value !== "";

// Series entries are keyed "0", "1", ... newest first. With no `at`, the
// newest reading if it is recent; with `at`, the newest reading in the hour
// up to and including `at` (stations report hourly or every 10 minutes).
const readingAt = (series, at, now) => {
  if (at === null) {
    const latest = series?.["0"];
    if (!hasValue(latest)) return null;
    const time = parseTime(latest.endtime);
    return now - time.getTime() > MAX_AGE_MS ? null : { ...latest, time };
  }
  for (let i = 0; series?.[i]; i++) {
    const reading = series[i];
    const time = parseTime(reading.endtime);
    if (at - time.getTime() >= HOUR_MS) break; // older than the window
    if (time.getTime() <= at && hasValue(reading)) return { ...reading, time };
  }
  return null;
};

// Returns { level, color, label, worst: { param, value, unit }, readings }
// Each reading has its own level, or null when the pollutant has no bands.
// `at` (ms since epoch) picks a past hour; null means the latest readings.
export const stationSeverity = (station, at = null, now = Date.now()) => {
  let worst = null;
  const readings = [];

  Object.entries(station.parameters || {}).forEach(([param, series]) => {
    const reading = readingAt(series, at, now);
    if (!reading) return;
    const { time } = reading;

    const value = parseFloat(reading.value);
    const level = levelFor(param, value);
    readings.push({ param, value, unit: series.unit, time, level });

    if (level !== null && (!worst || level > worst.level)) {
      worst = { level, param, value, unit: series.unit };
    }
  });

  if (!worst) return { ...NO_DATA, level: null, worst: null, readings };
  return { ...LEVELS[worst.level], level: worst.level, worst, readings };
};

// Full hours today that have at least one reading at any station, oldest
// first. Stations report hourly or every 10 minutes; a reading counts towards
// the hour it falls in. Icelandic time is UTC.
export const hoursWithData = (stations, now = Date.now()) => {
  const midnight = new Date(now).setUTCHours(0, 0, 0, 0);
  const hours = new Set();
  stations.forEach((station) =>
    Object.values(station.parameters || {}).forEach((series) => {
      for (let i = 0; series?.[i]; i++) {
        if (!hasValue(series[i])) continue;
        const time = parseTime(series[i].endtime).getTime();
        if (time < midnight) break; // newest first, so the rest are older
        if (time <= now) hours.add(time - (time % HOUR_MS));
      }
    }),
  );
  return [...hours].sort((a, b) => a - b);
};
